import { View, type ViewProps } from 'react-native';
import Svg, { Circle, G, Path } from 'react-native-svg';

import { AnimatedRoutePath } from '@/components/brand/animated-route-path';
import {
  routePreviewPath,
  routePreviewPathLength,
  type RouteLineString,
} from '@sixam/shared/route-core';
import { cn } from '@sixam/ui/utils';

const ROUTE_PATH =
  'M1.73 353.38 L78.27 325.23 L30.76 196.79 L90.58 175.67 L71.23 150.16 L96.74 143.12 L83.54 116.73 L129.29 125.53 L113.46 92.1 L129.29 84.18 L160.08 129.05 L147.77 84.18 L173.28 101.77 L220.79 34.91 L240.14 34.91 L248.06 5 L1296.05 47.18 L1367.82 69.26 L1418.39 54.51 L1427.33 71.28 L1517.45 122.6 L1525.58 152.63 L1525.58 166.4 L1528.71 179.19 L1495.54 208.33 L1493.67 191.43 L1511.81 170.16 L1568.14 167.03 L1574.62 168.35 L1598.8 173.29 L1598.8 161.4 L1581.28 153.89 L1575.65 142 L1481.15 167.65 L1465.5 205.83 L1484.28 498.71 L1802.81 486.19 L1788.42 135.11 L1703.94 137.62 L1695.8 170.78 L1678.9 198.32 L1609.44 192.06 L1605.06 173.29';
const ROUTE_LENGTH = 3657;
const ROUTE_STROKE = '#17ff27';
const ROUTE_START = { x: 1.73, y: 353.38 } as const;
const DYNAMIC_ROUTE_WIDTH = 1600;
const DYNAMIC_ROUTE_HEIGHT = 500;

type RouteLinePreviewProps = ViewProps &
  Readonly<{
    geometry?: RouteLineString | null;
    name?: string;
  }>;

function routePoint(path: string, position: 'first' | 'last') {
  const points = [...path.matchAll(/[ML]([\d.-]+),([\d.-]+)/g)];
  const point = position === 'first' ? points[0] : points.at(-1);
  return point ? { x: Number(point[1]), y: Number(point[2]) } : ROUTE_START;
}

function RouteLinePreview({ className, geometry, name, ...props }: RouteLinePreviewProps) {
  const dynamicPath = geometry
    ? routePreviewPath(geometry.coordinates, DYNAMIC_ROUTE_WIDTH, DYNAMIC_ROUTE_HEIGHT, 44)
    : '';
  const routePath = dynamicPath || ROUTE_PATH;
  const routeLength = dynamicPath ? routePreviewPathLength(dynamicPath) : ROUTE_LENGTH;
  const start = dynamicPath ? routePoint(dynamicPath, 'first') : ROUTE_START;
  const finish = dynamicPath ? routePoint(dynamicPath, 'last') : ROUTE_START;
  const viewBox = dynamicPath
    ? `0 0 ${DYNAMIC_ROUTE_WIDTH} ${DYNAMIC_ROUTE_HEIGHT}`
    : '-48 -32 1903.81 583.71';

  return (
    <View
      accessibilityLabel={name ? `Animated route for ${name}` : 'Weekly Monday 6am route line'}
      className={cn('w-full items-center justify-center', className)}
      {...props}
    >
      <Svg
        accessibilityRole="image"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={viewBox}
        width="100%"
      >
        <Path
          d={routePath}
          fill="none"
          opacity={0.18}
          stroke={ROUTE_STROKE}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={18}
        />
        <AnimatedRoutePath
          d={routePath}
          fill="none"
          length={routeLength}
          stroke={ROUTE_STROKE}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={10}
        />
        <G transform={`translate(${finish.x} ${finish.y})`}>
          <Path
            d="M0 0C-17 -19 -19 -27 -19 -38C-19 -50 -10.5 -59 0 -59C10.5 -59 19 -50 19 -38C19 -27 17 -19 0 0Z"
            fill="#050505"
            stroke="#FFFFFF"
            strokeLinejoin="round"
            strokeWidth={4}
          />
          <Path
            d="M-7.5 -38L-2.4 -32.8L8.5 -44"
            fill="none"
            stroke="#FFFFFF"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={4}
          />
        </G>
        <G transform={`translate(${start.x} ${start.y})`}>
          <Circle r={15} fill="rgba(0, 0, 0, 0.58)" stroke="#FFFFFF" strokeWidth={5} />
          <Circle r={6} fill={ROUTE_STROKE} stroke="#101010" strokeWidth={2} />
        </G>
      </Svg>
    </View>
  );
}

export { RouteLinePreview, ROUTE_PATH };
